import { isFiniteNumber } from "@/utils/numberHelpers"

export type TargetParamBw = 125 | 250 | 500 | 406 | 812 | 1625
export type TargetChirpType = "up-only" | "down-only" | "up-down"

export interface TargetParamModeDto {
  id: string
  sf: number | null
  bw: TargetParamBw | null
  chirp_type?: TargetChirpType | null
  sweep_iteration?: boolean
  sweep_time?: number
  is_active: boolean
  order: number
  created_at: string
  updated_at: string | null
}

export interface TargetParamDto {
  id: string
  freq_from: number
  freq_to: number
  bw: number | null
  sf: number | null
  chirp_type?: TargetChirpType | null
  sweep_iteration?: boolean
  sweep_time?: number
  is_video?: boolean
  polarization_angle?: 0 | 45 | 90 | null
  modes?: TargetParamModeDto[]
  created_at: string
  updated_at: string | null
}

export interface TargetLiveDto {
  id: string
  name: string | null
  is_template: boolean
  last_lng: number | null
  last_lat: number | null
  height_above_ground?: number | null
  created_at: string
  updated_at: string | null
  creator_username: string
  status: string
  status_changed_at?: string | null
  target_lifetime_seconds?: number | null
  params?: TargetParamDto[]
  edited_at?: string | null
  edited_by?: string | null
  sync_stations?: boolean
  rfmd?: number[]
}

export interface TargetDetailDto extends TargetLiveDto {
  params: TargetParamDto[]
}

/** Paginated list response from GET /targets/ (no stations_online). */
export interface TargetListPageDto {
  items: TargetLiveDto[]
  total: number
  page: number
  size: number
  pages: number
}

export interface TargetParamMode {
  id?: string
  sf: number | null
  bw: TargetParamBw | null
  chirp_type?: TargetChirpType | null
  sweep_iteration?: boolean
  sweep_time?: number
  is_active: boolean
  order: number
  created_at?: string
  updated_at?: string | null
}

export interface TargetParam {
  id?: string
  freq_from: number
  freq_to: number
  bw: number | null
  sf: number | null
  chirp_type?: TargetChirpType | null
  sweep_iteration?: boolean
  sweep_time?: number
  is_video: boolean
  polarization_angle: 0 | 45 | 90 | null
  modes?: TargetParamMode[]
  created_at?: string
  updated_at?: string | null
}

export interface Target {
  id?: string
  station_id?: string
  name?: string
  isTemplate: boolean
  lat: number
  lng: number
  height_above_ground?: number
  height_above_sea?: number
  status?: string
  status_changed_at?: string | null
  target_lifetime_seconds?: number | null
  created_at?: string
  updated_at?: string | null
  creator_username?: string
  edited_at?: string | null
  edited_by?: string | null
  params?: TargetParam[]
  sync_stations?: boolean
  rfmd?: number[]
  // Legacy optional fields still referenced by table UI
  type?: string
  height?: number
}

export interface TargetListPage {
  items: Target[]
  total: number
  page: number
  size: number
  pages: number
}

export interface TargetCreateRequest {
  name?: string
  is_template: boolean
  last_lng: number
  last_lat: number
  height_above_sea?: number
  height_above_ground?: number
  add_target_history?: boolean
  sync_stations?: boolean
  rfmd?: number[]
  params: TargetCreateParamRequest[]
}

export interface TargetPatchRequest {
  lat?: number
  lng?: number
  height_above_ground?: number
  height_above_sea?: number
  /** @deprecated legacy fields kept for transitional callers */
  name?: string
  is_template?: boolean
  last_lng?: number
  last_lat?: number
  status?: string
  sync_stations?: boolean
  rfmd?: number[]
}

export interface TargetParamModeCreateRequest {
  sf: number | null
  bw: TargetParamBw | null
  chirp_type?: TargetChirpType | null
  sweep_iteration?: boolean
  sweep_time?: number
  is_active: boolean
  order: number
}

export interface TargetParamModeUpdateRequest {
  id?: string
  sf?: number | null
  bw?: TargetParamBw | null
  chirp_type?: TargetChirpType | null
  sweep_iteration?: boolean
  sweep_time?: number
  is_active?: boolean
  order?: number
}

export interface TargetCoordinatesUpdate {
  id: string
  lat: number
  lng: number
  edited_at?: string | null
  edited_by?: string | null
}

export interface TargetCreateParamRequest {
  freq_from: number
  freq_to: number
  bw: number | null
  sf: number | null
  chirp_type?: TargetChirpType | null
  sweep_iteration?: boolean
  sweep_time?: number
  is_video: boolean
  polarization_angle: 0 | 45 | 90 | null
  modes?: TargetParamModeCreateRequest[]
}

const toFiniteNumber = (value: unknown, fallback = 0): number => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number.parseFloat(value)
        : Number.NaN

  return isFiniteNumber(parsed) ? parsed : fallback
}

export const mapTargetParamModeDtoToModel = (
  dto: TargetParamModeDto,
): TargetParamMode => ({
  id: dto.id,
  sf: dto.sf,
  bw: dto.bw,
  chirp_type: dto.chirp_type ?? "up-down",
  sweep_iteration: dto.sweep_iteration ?? false,
  ...(typeof dto.sweep_time === "number" ? { sweep_time: dto.sweep_time } : {}),
  is_active: dto.is_active,
  order: dto.order,
  created_at: dto.created_at,
  updated_at: dto.updated_at,
})

export const mapTargetParamDtoToModel = (dto: TargetParamDto): TargetParam => ({
  id: dto.id,
  freq_from: dto.freq_from,
  freq_to: dto.freq_to,
  bw: dto.bw,
  sf: dto.sf,
  chirp_type: dto.chirp_type ?? "up-down",
  sweep_iteration: dto.sweep_iteration ?? false,
  ...(typeof dto.sweep_time === "number" ? { sweep_time: dto.sweep_time } : {}),
  is_video: dto.is_video ?? false,
  polarization_angle: Object.prototype.hasOwnProperty.call(
    dto,
    "polarization_angle",
  )
    ? (dto.polarization_angle ?? null)
    : 45,
  modes: (dto.modes ?? []).map(mapTargetParamModeDtoToModel),
  created_at: dto.created_at,
  updated_at: dto.updated_at,
})

export const mapTargetLiveDtoToModel = (dto: TargetLiveDto): Target => ({
  id: dto.id,
  name: dto.name ?? "",
  isTemplate: dto.is_template ?? false,
  lat: toFiniteNumber(dto.last_lat),
  lng: toFiniteNumber(dto.last_lng),
  height_above_ground:
    typeof dto.height_above_ground === "number"
      ? dto.height_above_ground
      : undefined,
  height:
    typeof dto.height_above_ground === "number"
      ? dto.height_above_ground
      : undefined,
  status: dto.status,
  status_changed_at: dto.status_changed_at ?? null,
  target_lifetime_seconds: dto.target_lifetime_seconds ?? null,
  created_at: dto.created_at,
  updated_at: dto.updated_at,
  creator_username: dto.creator_username,
  edited_at: dto.edited_at ?? null,
  edited_by: dto.edited_by ?? null,
  sync_stations: dto.sync_stations ?? false,
  rfmd: dto.rfmd ?? [],
  params: (dto.params ?? []).map(mapTargetParamDtoToModel),
})

export const mapTargetDetailDtoToModel = (dto: TargetDetailDto): Target => ({
  ...mapTargetLiveDtoToModel(dto),
})

export const mapTargetListPageDtoToModel = (
  dto: TargetListPageDto,
): TargetListPage => ({
  items: (dto.items ?? []).map(mapTargetLiveDtoToModel),
  total: dto.total ?? 0,
  page: dto.page ?? 0,
  size: dto.size ?? 0,
  pages: dto.pages ?? 0,
})

const mapParamModesToCreateRequest = (
  modes: TargetParamModeCreateRequest[] | undefined,
) =>
  modes?.map((mode) => ({
    sf: mode.sf,
    bw: mode.bw,
    chirp_type: mode.chirp_type ?? "up-down",
    sweep_iteration: mode.sweep_iteration ?? false,
    ...(mode.sweep_iteration && typeof mode.sweep_time === "number"
      ? { sweep_time: mode.sweep_time }
      : {}),
    is_active: mode.is_active,
    order: mode.order,
  }))

export const mapTargetCreateToRequest = (target: {
  name?: string
  isTemplate?: boolean
  lat: number
  lng: number
  height_above_sea?: number
  height_above_ground?: number
  add_target_history?: boolean
  sync_stations?: boolean
  rfmd?: number[]
  params: TargetCreateParamRequest[]
}): TargetCreateRequest => ({
  name: target.name,
  is_template: target.isTemplate ?? false,
  last_lng: target.lng,
  last_lat: target.lat,
  height_above_sea: target.height_above_sea,
  height_above_ground: target.height_above_ground,
  add_target_history: target.add_target_history,
  sync_stations: target.sync_stations,
  rfmd: target.rfmd,
  params: target.params.map((param) => ({
    freq_from: param.freq_from,
    freq_to: param.freq_to,
    bw: param.bw,
    sf: param.sf,
    ...(param.chirp_type ? { chirp_type: param.chirp_type } : {}),
    sweep_iteration: param.sweep_iteration ?? false,
    ...(param.sweep_iteration && typeof param.sweep_time === "number"
      ? { sweep_time: param.sweep_time }
      : {}),
    is_video: param.is_video,
    polarization_angle: param.polarization_angle,
    ...(param.modes?.length
      ? { modes: mapParamModesToCreateRequest(param.modes) }
      : {}),
  })),
})

export const mapTargetPatchToRequest = (target: {
  name?: string
  isTemplate?: boolean
  lat?: number
  lng?: number
  height_above_ground?: number
  height_above_sea?: number
  status?: string
  sync_stations?: boolean
  rfmd?: number[]
}): TargetPatchRequest => {
  const payload: TargetPatchRequest = {}

  if (typeof target.lat !== "undefined") payload.lat = target.lat
  if (typeof target.lng !== "undefined") payload.lng = target.lng
  if (typeof target.height_above_ground !== "undefined") {
    payload.height_above_ground = target.height_above_ground
  }
  if (typeof target.height_above_sea !== "undefined") {
    payload.height_above_sea = target.height_above_sea
  }

  return payload
}

export const mapTargetSseLocation = (
  message: {
    key?: string
    identifier?: string
    data?: any
  } | null,
): TargetCoordinatesUpdate | null => {
  if (!message || !message.key) {
    return null
  }

  const key = String(message.key)
  const payload = message.data ?? {}

  const resolveCoords = (value: any) => {
    if (!value || typeof value !== "object") return null
    const lat = toFiniteNumber(value.lat, Number.NaN)
    const lng = toFiniteNumber(value.lng, Number.NaN)
    if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) return null
    return {
      lat,
      lng,
      edited_at:
        typeof value.edited_at === "string" ? value.edited_at : undefined,
      edited_by:
        typeof value.edited_by === "string" || value.edited_by === null
          ? value.edited_by
          : undefined,
    }
  }

  if (key.startsWith("targets:")) {
    const id = key.slice("targets:".length)
    const coords = resolveCoords(payload)
    if (!id || !coords) return null
    return { id, ...coords }
  }

  if (key === "targets") {
    const id = message.identifier || payload.target_id
    const coords = resolveCoords(payload)
    if (!id || !coords) return null
    return { id, ...coords }
  }

  if (key === "new_target_location") {
    const id = payload.target_id || message.identifier
    const locationPayload =
      payload.location && typeof payload.location === "object"
        ? {
            ...payload.location,
            edited_at: payload.location.edited_at ?? payload.edited_at,
            edited_by: payload.location.edited_by ?? payload.edited_by,
          }
        : payload
    const coords = resolveCoords(locationPayload)
    if (!id || !coords) return null
    return { id, ...coords }
  }

  return null
}
